import React, { useMemo, useState } from 'react'
import { Box, MenuItem, Select, TextField, Typography } from '@mui/material'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import PeopleIcon from '@mui/icons-material/People'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import GridViewIcon from '@mui/icons-material/GridView'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import { useGetRetentionDashboardQuery } from '../../redux/growthosRetentionApiSlice'
import { useListIngestRunsQuery } from '../../redux/growthosConnectorsApiSlice'
import { GX_BORDER, GX_INK_500, GX_INK_700, GX_INK_900, GX_PAGE_SX, GX_COLORS, GxSyncStatusBar, GxPill } from '../growthos-shared/GrowthOsUi'
import { GxPagedTable } from '../growthos-shared/GxPagedTable'
import { integer, currency, percentage, comparison, shortDate, GrowthPct } from '../growthos-shared/gxFormat'
import { KpiTile, ScopeTag, Panel } from '../growthos-sme/SmeParityUi'

const INK_400 = '#94a3b8'

const PERIODS = [
  { value: 'mtd', label: 'MTD' }, { value: 'last_month', label: 'Last Month' },
  { value: 'last_3m', label: 'Last 3M' }, { value: 'last_6m', label: 'Last 6M' },
  { value: 'ytd', label: 'YTD' }, { value: 'all', label: 'All time' }, { value: 'custom', label: 'Custom' },
]

const STATUS_TINT = {
  Healthy: { bg: 'rgba(5,150,105,0.12)', fg: '#047857', icon: <FavoriteBorderIcon sx={{ fontSize: 14 }} /> },
  Declining: { bg: 'rgba(217,119,6,0.14)', fg: '#b45309', icon: <ShowChartIcon sx={{ fontSize: 14 }} /> },
  Churned: { bg: 'rgba(225,29,72,0.12)', fg: '#be123c', icon: <RemoveShoppingCartIcon sx={{ fontSize: 14 }} /> },
  New: { bg: 'rgba(79,70,229,0.12)', fg: '#4338ca', icon: <AutoAwesomeIcon sx={{ fontSize: 14 }} /> },
}
const STATUS_ORDER = ['Healthy', 'Declining', 'Churned', 'New']
// Bucket precedence: Churned → New → Declining → Healthy.
const STATUS_HELP = {
  Healthy: 'Shipping recently and window revenue at or above the comparable prior window.',
  Declining: 'Window revenue below the comparable prior window (still shipping).',
  Churned: 'No positive shipment in the 30 days ending at the ProShip data-through date. Takes precedence over New — a brand that onboarded recently but has already gone quiet still reads as churned.',
  New: 'Onboarded in the last 45 days — first-ever positive shipment within 45 days of the ProShip data-through date (independent of the selected period).',
}
const VIEW_LABEL = { active: 'Active Brands', reactivated: 'Reactivated Brands', Healthy: 'Healthy', Declining: 'Declining', Churned: 'Churned', New: 'New' }

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const cohortLabel = (ym) => { const [y, m] = ym.split('-'); return `${MON[Number(m) - 1]} ${y}` }

// Retention heat scale — high = green, low = red.
const cohortTint = (pct) => {
  if (pct == null) return { background: 'transparent', color: '#cbd5e1' }
  if (pct >= 90) return { background: 'rgba(16,185,129,0.20)', color: '#047857' }
  if (pct >= 75) return { background: 'rgba(52,211,153,0.18)', color: '#047857' }
  if (pct >= 60) return { background: 'rgba(250,204,21,0.22)', color: '#a16207' }
  if (pct >= 45) return { background: 'rgba(251,146,60,0.22)', color: '#c2410c' }
  if (pct >= 30) return { background: 'rgba(251,113,133,0.20)', color: '#be123c' }
  return { background: 'rgba(244,63,94,0.16)', color: '#be123c' }
}

// statusCounts arrives as {healthy, declining, churned, newCount}.
const statusCount = (counts, bucket) => {
  if (!counts) return null
  if (bucket === 'Healthy') return counts.healthy
  if (bucket === 'Declining') return counts.declining
  if (bucket === 'Churned') return counts.churned
  if (bucket === 'New') return counts.newCount
  return null
}

function StatusBadge({ status }) {
  const t = STATUS_TINT[status] || STATUS_TINT.Healthy
  return <Box component="span" sx={{ borderRadius: '999px', px: 1, py: 0.25, fontSize: 12, fontWeight: 600, bgcolor: t.bg, color: t.fg }}>{status}</Box>
}

function Section({ title, subtitle, icon, color, count, right, children }) {
  return (
    <Panel sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, px: 2.5, pb: 1.5, pt: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex', color }}>{icon}</Box>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: GX_INK_700 }}>
              {title}
              {count != null && <Box component="span" sx={{ ml: 0.5, fontSize: 12, fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: INK_400 }}>({integer(count)})</Box>}
            </Typography>
            {subtitle && <Typography sx={{ fontSize: 12, color: INK_400 }}>{subtitle}</Typography>}
          </Box>
        </Box>
        {right}
      </Box>
      <Box sx={{ px: 2.5, pb: 2.5 }}>{children}</Box>
    </Panel>
  )
}

export default function RetentionPage() {
  const [period, setPeriod] = useState('mtd')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  // One filter drives the matrix — a KPI card ('active' / 'reactivated') or a status bucket.
  const [view, setView] = useState(null)
  const [cohortView, setCohortView] = useState('pct')

  const customReady = period !== 'custom' || Boolean(customFrom && customTo)
  const { data, isFetching, error } = useGetRetentionDashboardQuery(
    { period, from: period === 'custom' ? customFrom : undefined, to: period === 'custom' ? customTo : undefined },
    { skip: !customReady },
  )
  const { data: runsData } = useListIngestRunsQuery()
  const loading = isFetching && !data
  const dataThrough = data?.coverage?.proship?.dataThrough

  const matrixRows = useMemo(() => {
    const brands = data?.brands || []
    if (!view) return brands
    if (view === 'active') return brands.filter((b) => b.shipments > 0)
    // Jackson strips "is" from the primitive isReactivated getter -> wire field "reactivated".
    if (view === 'reactivated') return brands.filter((b) => b.reactivated)
    return brands.filter((b) => b.status === view)
  }, [data, view])
  const toggle = (v) => setView((cur) => (cur === v ? null : v))

  const matrixColumns = [
    { key: 'brand', header: 'Brand', title: 'Brand name (ProShip / Data → Brands).', sortValue: (b) => b.brandName, cell: (b) => <Box component="span" sx={{ fontWeight: 600 }}>{b.brandName}</Box> },
    { key: 'poc', header: 'Sales POC', title: "The brand's assigned Sales POC.", sortValue: (b) => b.poc, cell: (b) => b.poc },
    { key: 'city', header: 'City', title: "Brand city — manual value, else the linked HubSpot company's city.", sortValue: (b) => b.city ?? '', cell: (b) => b.city ?? '—' },
    { key: 'closed', header: 'Closed Date', title: 'Latest won/onboarded linked HubSpot deal close date.', sortValue: (b) => b.closedDate ?? '', cell: (b) => shortDate(b.closedDate) },
    { key: 'first', header: 'First Shipment', title: "The brand's first-ever positive ProShip shipment.", sortValue: (b) => b.firstShipmentDate ?? '', cell: (b) => shortDate(b.firstShipmentDate) },
    { key: 'last', header: 'Last Shipment', title: 'Most recent positive ProShip shipment.', sortValue: (b) => b.lastShipmentDate ?? '', cell: (b) => shortDate(b.lastShipmentDate) },
    { key: 'ship', header: 'Shipments', title: 'ProShip shipments in the selected window.', align: 'right', sortValue: (b) => b.shipments, cell: (b) => integer(b.shipments) },
    { key: 'pship', header: 'Prev Shipments', title: 'Shipments in the comparable prior window.', align: 'right', sortValue: (b) => b.prevShipments, cell: (b) => integer(b.prevShipments) },
    { key: 'shipPct', header: 'Ship %Δ', title: 'Shipment % change vs the comparable prior window.', align: 'right', sortValue: (b) => b.shipmentChangePct, cell: (b) => <GrowthPct pct={b.shipmentChangePct} /> },
    { key: 'rev', header: 'Revenue', title: 'ProShip revenue (₹) in the selected window.', align: 'right', sortValue: (b) => b.revenue, cell: (b) => currency(b.revenue) },
    { key: 'prev', header: 'Prev Revenue', title: 'Revenue in the comparable prior window.', align: 'right', sortValue: (b) => b.prevRevenue, cell: (b) => currency(b.prevRevenue) },
    { key: 'revPct', header: 'Rev %Δ', title: 'Revenue % change vs the comparable prior window.', align: 'right', sortValue: (b) => b.revenueChangePct, cell: (b) => <GrowthPct pct={b.revenueChangePct} /> },
    { key: 'status', header: 'Status', title: 'Churned → New → Declining → Healthy, evaluated in that order.', sortValue: (b) => b.status, cell: (b) => <StatusBadge status={b.status} /> },
    { key: 'days', header: 'Days Since Last Shipment', title: 'Days from the last positive shipment to the ProShip data-through date.', align: 'right', sortValue: (b) => b.daysSinceLastShipment ?? -1, cell: (b) => (b.daysSinceLastShipment == null ? 'Never' : integer(b.daysSinceLastShipment)) },
  ]

  const cohortColumns = [
    {
      key: 'cohort', header: 'Cohort', title: 'Month of the brands’ first-ever positive shipment.', sortValue: (r) => r.cohort,
      cell: (r) => (
        <Box>
          <Box sx={{ fontWeight: 600 }}>{cohortLabel(r.cohort)}</Box>
          <Box sx={{ fontSize: 12, color: INK_400 }}>{integer(r.brandCount)} brands</Box>
        </Box>
      ),
    },
    ...(data?.cohorts?.monthLabels || []).map((label, k) => ({
      key: label,
      header: label,
      title: `Brands from the cohort still shipping ${k} month${k === 1 ? '' : 's'} after their first shipment.`,
      align: 'center',
      cell: (r) => {
        const cell = r.cells[k]
        if (!cell || cell.count == null) return <Box component="span" sx={{ color: '#cbd5e1' }}>–</Box>
        return (
          <Box component="span" sx={{ ...cohortTint(cell.pct), display: 'inline-block', minWidth: 52, borderRadius: '6px', px: 1, py: 0.5, fontSize: 12, fontWeight: 600 }}>
            {cohortView === 'pct' ? (cell.pct == null ? '–' : `${cell.pct}%`) : integer(cell.count)}
          </Box>
        )
      },
    })),
  ]

  const k = data?.kpis
  return (
    <Box sx={GX_PAGE_SX}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <GxSyncStatusBar runs={runsData?.runs || []} />
        {dataThrough && <Box sx={{ mb: 2 }}><GxPill color={GX_COLORS.indigo}>ProShip data through: {shortDate(dataThrough)}</GxPill></Box>}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1.5, mb: 2.5 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: '#4f46e5' }}>
            <FavoriteBorderIcon sx={{ fontSize: 15 }} /> Retention · Key Account Management
          </Box>
          <Typography sx={{ mt: 0.5, fontSize: 24, fontWeight: 700, color: GX_INK_900 }}>Retention (KAM)</Typography>
          <Typography sx={{ fontSize: 13, color: GX_INK_500 }}>
            Every ProShip-shipping brand — who is healthy, slipping, gone, or back. Churn = no positive shipment in 30 days.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Select size="small" value={period} onChange={(e) => setPeriod(e.target.value)} sx={{ minWidth: 140, fontSize: 13, bgcolor: '#fff' }}>
            {PERIODS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </Select>
          {period === 'custom' && (
            <>
              <TextField size="small" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
              <TextField size="small" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
            </>
          )}
          {data?.window && <ScopeTag label={`Showing: ${shortDate(data.window.from)} – ${shortDate(data.window.to)}`} />}
        </Box>
      </Box>

      {error && (
        <Panel sx={{ p: 2, mb: 2, fontSize: 13, color: '#e11d48' }}>Failed to load — {error?.data?.message || error?.message || `HTTP ${error?.status}`}</Panel>
      )}

      {/* KPI row — every card filters the matrix below */}
      <Box sx={{ display: 'grid', gap: 2, mb: 2.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' } }}>
        <KpiTile label="Active Brands" icon={<PeopleIcon sx={{ fontSize: 16 }} />} accent="indigo" loading={loading}
          value={k ? integer(k.activeBrands.value) : undefined}
          sub={k ? `${comparison(k.activeBrands, integer)} · click` : undefined}
          active={view === 'active'} onClick={() => toggle('active')}
          definition="Brands with at least one positive ProShip shipment in the selected window, vs the comparable prior window. Click to list them in the matrix below." />
        <KpiTile label="Declining Brands" icon={<TrendingDownIcon sx={{ fontSize: 16 }} />} accent="amber" loading={loading}
          value={k ? integer(k.decliningBrands) : undefined}
          sub={k ? `${currency(k.decliningRevenueDrop)} below prior window · click` : undefined}
          active={view === 'Declining'} onClick={() => toggle('Declining')}
          definition="Brands still shipping whose window revenue is below the comparable prior window (and not churned or newly onboarded). Click to list them in the matrix below." />
        <KpiTile label="Churned Brands" icon={<RemoveShoppingCartIcon sx={{ fontSize: 16 }} />} accent="rose" loading={loading}
          value={k ? integer(k.churnedBrands) : undefined}
          sub={k ? `no shipment since ${shortDate(k.churnCutoff)} · click` : undefined}
          active={view === 'Churned'} onClick={() => toggle('Churned')}
          definition="Brands with no positive shipment in the 30 days ending at the ProShip data-through date — the same churn anchor used on Executive and SME. Click to list them in the matrix below." />
        <KpiTile label="Reactivated Brands" icon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />} accent="teal" loading={loading}
          value={k ? integer(k.reactivatedBrands) : undefined}
          sub={k ? `${[
            k.avgDormantDays != null ? `back after ~${integer(k.avgDormantDays)}d dormant` : null,
            k.winbackRatePct != null ? `${percentage(k.winbackRatePct)} win-back` : null,
          ].filter(Boolean).join(' · ')} · click` : undefined}
          active={view === 'reactivated'} onClick={() => toggle('reactivated')}
          definition="Brands shipping in this window that shipped ZERO in the comparable prior window and first shipped before it — a revival, not a new brand. Reported as average dormancy (how long they were quiet before returning) and win-back rate (share of last window's idle brands that came back) rather than recovered revenue, which keeps growing after the revival and so reads as a moving target. Click to list them in the matrix below." />
      </Box>

      {/* Brand Health Matrix */}
      <Section
        title="Brand Health Matrix" icon={<GridViewIcon sx={{ fontSize: 17 }} />} color="#7c3aed" count={matrixRows.length}
        subtitle="Window vs comparable prior window, per brand. Click a card above or a bucket below to filter."
        right={view ? (
          <Box component="button" type="button" onClick={() => setView(null)}
            sx={{ borderRadius: '8px', border: `1px solid ${GX_BORDER}`, px: 1.25, py: 0.5, fontSize: 12, fontWeight: 500, bgcolor: 'transparent', cursor: 'pointer', color: GX_INK_700 }}>
            Clear filter · {VIEW_LABEL[view] || view}
          </Box>
        ) : undefined}
      >
        <Box sx={{ mb: 2, display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' } }}>
          {STATUS_ORDER.map((s) => {
            const t = STATUS_TINT[s]
            const active = view === s
            const n = statusCount(data?.statusCounts, s)
            return (
              <Box key={s} component="button" type="button" onClick={() => toggle(s)} title={STATUS_HELP[s]}
                sx={{
                  textAlign: 'left', cursor: 'pointer', font: 'inherit', borderRadius: '12px', border: '1px solid', p: 1.5,
                  borderColor: active ? t.fg : GX_BORDER, bgcolor: active ? t.bg : 'transparent', boxShadow: active ? `inset 0 0 0 1px ${t.fg}` : 'none',
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: t.fg }}>
                  {t.icon} {s}
                </Box>
                <Typography sx={{ mt: 0.5, fontSize: 24, fontWeight: 700, color: GX_INK_900, fontVariantNumeric: 'tabular-nums' }}>{n == null ? '—' : integer(n)}</Typography>
              </Box>
            )
          })}
        </Box>
        {/* Paged (25/50/100) with the body scrolling inside the card, as in the reference. */}
        <GxPagedTable variant="sme"
          rows={matrixRows}
          rowKey={(b) => b.brandId}
          columns={matrixColumns}
          filter={(b, q) => `${b.brandName} ${b.poc} ${b.city ?? ''} ${b.status}`.toLowerCase().includes(q)}
          filterPlaceholder="Filter brand, POC, city, status…"
          emptyText={loading ? 'Loading…' : 'No brands match this bucket.'}
        />
      </Section>

      {/* Cohort Retention */}
      <Section
        title="Cohort Retention Curves" icon={<ShowChartIcon sx={{ fontSize: 17 }} />} color="#0d9488"
        subtitle="Latest 12 cohorts — shipment retention over time, independent of the selected period."
        right={(
          <Box sx={{ display: 'inline-flex', borderRadius: '12px', border: `1px solid ${GX_BORDER}`, p: 0.25 }}>
            {['pct', 'absolute'].map((v) => (
              <Box key={v} component="button" type="button" onClick={() => setCohortView(v)}
                sx={{
                  border: 'none', cursor: 'pointer', font: 'inherit', borderRadius: '8px', px: 1.5, py: 0.75, fontSize: 12, fontWeight: 600,
                  ...(cohortView === v ? { bgcolor: '#eef0fd', color: '#4f46e5' } : { bgcolor: 'transparent', color: GX_INK_500 }),
                }}>
                {v === 'pct' ? '% Retention' : 'Absolute'}
              </Box>
            ))}
          </Box>
        )}
      >
        {/* Fixed 12 rows — no vertical scroll, no pager; scrolls horizontally for M0–M12. */}
        <GxPagedTable variant="sme"
          rows={data?.cohorts?.rows || []}
          rowKey={(r) => r.cohort}
          columns={cohortColumns}
          paginate={false}
          emptyText={loading ? 'Loading…' : 'No cohorts yet.'}
        />
      </Section>
    </Box>
  )
}
